import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { map, Observable } from 'rxjs';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectAuth } from '@state/auth/auth.selectors';
import { setAuthModal } from '@state/auth/auth.actions';

import {
  ACCOUNT_SETUP_MIN_DATE,
  REVIEW_CBSA_PREFERENCES_MIN_DATE,
} from '@core/constants/auth';

export type CanActivateRes =
  | Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree;

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private store: Store<AppState>,
    private router: Router
  ) {}

  /**
   * Logic to show the auth modal for desktop should also
   * be applied to the /modules/home/pages/home-map/map-view/map-view.component.ts
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): CanActivateRes {
    return this.store.select(selectAuth).pipe(
      map((auth) => {
        const magicToken =
          route.queryParams['magicToken'] || route.queryParams['magic-token'];
        const strategy = route.queryParams['strategy'];

        // Detect if this is an embed route to handle errors differently
        const isEmbedRoute = state.url.startsWith('/embed/');

        if (isEmbedRoute && !magicToken && !auth.token) {
          return this.router.createUrlTree(['/embed/unauthenticated']);
        } else if (magicToken) {
          const queryParams = { ...route.queryParams };
          delete queryParams['magicToken'];
          delete queryParams['magic-token'];

          // Reconstruct the originalUrl without magicToken
          const urlWithoutMagicToken = this.router.createUrlTree(
            [state.url.split('?')[0]],
            { queryParams }
          );

          const cleanOriginalUrl =
            this.router.serializeUrl(urlWithoutMagicToken);

          return this.router.createUrlTree(
            [!auth.token ? '/auth/magic-token' : '/reauthenticate'],
            {
              queryParams: {
                token: magicToken,
                /**
                 * If there's strategy it means it's
                 * sign in from email/sms so we don't need
                 * the originalUrl
                 */
                originalUrl: !strategy ? cleanOriginalUrl : undefined,
                strategy,
                errType: isEmbedRoute ? 'embed' : undefined,
              },
            }
          );
        } else if (!auth.token) {
          return this.router.createUrlTree(['/lp']);
        } else if (state.url === '/logout') {
          return true;
        } else if (!auth.user?.phoneNumber) {
          this.store.dispatch(setAuthModal({ authModal: 'add-phone-number' }));
        } else if (
          !auth.user?.emailConfirmedAt ||
          !auth.user?.phoneNumberConfirmedAt
        ) {
          this.store.dispatch(setAuthModal({ authModal: 'verify-identity' }));
        } else if (
          (!auth.user?.preference || !auth.user.preference.roles) &&
          new Date(auth.user?.insertedAt) >= ACCOUNT_SETUP_MIN_DATE
        ) {
          this.store.dispatch(setAuthModal({ authModal: 'account-setup' }));
        } else if (auth?.user?.isActive === false) {
          return this.router.createUrlTree(['/auth/deactivated']);
          // If date if before REVIEW_CBSA_PREFERENCES_MIN_DATE, show the review cbsa preferences modal
        } else if (
          auth?.user?.preference &&
          (!auth?.user?.cbsaPreferences ||
            auth.user.cbsaPreferences.length === 0 ||
            new Date(auth.user?.cbsaPreferences[0]?.updatedAt) <
              REVIEW_CBSA_PREFERENCES_MIN_DATE)
        ) {
          this.store.dispatch(
            setAuthModal({ authModal: 'review-cbsa-preferences' })
          );
        }
        return true;
      })
    );
  }
}
